import React from 'react';
import {
  Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Stack, Typography, Chip, TextField, Select, MenuItem, FormControl, InputLabel, Skeleton
} from '@mui/material';
import { ReconcileAction, ACTION_LABELS } from '../Data/enums';

const actionColor = (a) => {
  switch (a) {
    case ReconcileAction.CONTINUE: return 'success';
    case ReconcileAction.MODIFY:   return 'warning';
    case ReconcileAction.STOP:     return 'error';
    case ReconcileAction.NEW:      return 'info';
    default: return 'default';
  }
};

export default function ReconciliationBoard({
  items = [],
  loading,
  onChangeAction,
  onChangeTarget,
  onChangeNote,
  onChangeReason,
}) {
  if (loading) return <Skeleton variant="rounded" height={320} />;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { backgroundColor: '#607d8b', color: '#fff', fontWeight: 'bold' } }}>
            <TableCell>Medication</TableCell>
            <TableCell>Current Plan</TableCell>
            <TableCell>Proposal</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, idx) => (
            <TableRow
              key={it.key}
              sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5', '&:hover': { backgroundColor: '#e3f2fd' } }}
            >
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={600}>{it.name}</Typography>
                  <Chip
                    size="small"
                    label={ACTION_LABELS[it.action] || it.action}
                    color={actionColor(it.action)}
                    variant="outlined"
                  />
                </Stack>
                {it.warnings?.length > 0 && (
                  <Typography variant="caption" color="warning.main">
                    {it.warnings.join(' • ')}
                  </Typography>
                )}
              </TableCell>

              <TableCell>
                {it.from ? (
                  <Stack spacing={0.5}>
                    <Typography variant="body2">{it.from.dose}</Typography>
                    <Typography variant="caption">{it.from.route} • {it.from.frequency}</Typography>
                  </Stack>
                ) : <Typography variant="caption" color="text.secondary">—</Typography>}
              </TableCell>

              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small" label="Dose" sx={{ width: 130 }}
                    value={it.to?.dose || ''}
                    onChange={(e) => onChangeTarget?.(it.key, { dose: e.target.value })}
                  />
                  <FormControl size="small" sx={{ width: 110 }}>
                    <InputLabel>Route</InputLabel>
                    <Select
                      label="Route"
                      value={it.to?.route || ''}
                      onChange={(e) => onChangeTarget?.(it.key, { route: e.target.value })}
                    >
                      {['PO','SC','INH','IM','IV'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: 120 }}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      label="Frequency"
                      value={it.to?.frequency || ''}
                      onChange={(e) => onChangeTarget?.(it.key, { frequency: e.target.value })}
                    >
                      {['QD','BID','TID','QHS','PRN','Q6H'].map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Stack>
              </TableCell>

              <TableCell>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={it.action}
                    onChange={(e) => onChangeAction?.(it.key, e.target.value)}
                  >
                    {Object.values(ReconcileAction).map(a =>
                      <MenuItem key={a} value={a}>{ACTION_LABELS[a] || a}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </TableCell>

              <TableCell>
                <TextField
                  size="small"
                  placeholder="Clinical justification..."
                  value={it.reason || ''}
                  onChange={(e) => onChangeReason?.(it.key, e.target.value)}
                  multiline
                  minRows={1}
                  maxRows={3}
                  sx={{ minWidth: 200 }}
                />
              </TableCell>

              <TableCell>
                <TextField
                  size="small" placeholder="Additional notes..."
                  value={it.note || ''}
                  onChange={(e) => onChangeNote?.(it.key, e.target.value)}
                />
              </TableCell>
            </TableRow>
          ))}

          {items.length === 0 && (
            <TableRow><TableCell colSpan={6} align="center">No items to reconcile</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
